import jsPDF from "jspdf";
import dateFormat from "dateformat";
import Logo from "../assets/images/logo.png";

// A text-based "Proof of Service" PDF for an order - meant to be attached
// as evidence when a customer disputes a card payment. Unlike the View
// Order screenshot PDF it flows across pages and only reports what the
// order record actually holds (this order's own Stripe charge, the
// assigned technician/fleet, and the recorded status history).

const ORDER_STATUS = {
  order_initiated: "Order Initiated",
  order_placed: "Order Placed",
  order_in_progress: "Order In Progress",
  order_completed: "Completed",
  order_canceled: "Order Canceled",
};

const TASK_STATUS = {
  "n/a": "Staff not yet assigned",
  staff_assigned: "Staff assigned",
  reached_at_location: "Staff reached location",
  setting_safety_items: "Setting safety items",
  access_situation: "Assessing situation",
  rectifiying_problem: "Rectifying problem",
  towing_required: "Towing required",
  task_completed: "Task completed",
};

const ROLE_LABEL = { admin: "Admin", staff: "Technician", customer: "Customer" };

const ORANGE = [255, 78, 0];
const DARK = [33, 37, 41];
const MUTED = [108, 117, 125];

// "by Jane Smith (Technician)", or just "by Admin" when no name was saved.
const changedBy = (entry) => {
  const role = ROLE_LABEL[entry?.changedByRole] || entry?.changedByRole;
  if (entry?.changedByName) {
    return ` (by ${entry.changedByName}${role ? `, ${role}` : ""})`;
  }
  return role ? ` (by ${role})` : "";
};

const fmtDate = (value) =>
  value ? dateFormat(value, "dd mmm yyyy, h:MM TT") : "";

const fullName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ");

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const checkLabel = (value) => {
  if (!value) return "";
  if (value === "pass") return "Passed";
  if (value === "fail") return "Failed";
  if (value === "unavailable" || value === "unchecked") return "Not checked";
  return value;
};

export const downloadProofOfServicePdf = async (order, extras = {}) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const labelW = 55;
  const valueX = margin + labelW;
  const valueW = pageW - margin - valueX;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - margin - 10) {
      doc.addPage();
      y = margin;
    }
  };

  const heading = (text) => {
    ensureSpace(14);
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...ORANGE);
    doc.text(text.toUpperCase(), margin, y);
    y += 2;
    doc.setDrawColor(230, 230, 230);
    doc.line(margin, y, pageW - margin, y);
    y += 6;
  };

  // Rows with an empty value are skipped so the document never shows
  // placeholder data.
  const row = (label, value) => {
    if (value === undefined || value === null || value === "") return;
    const lines = doc.splitTextToSize(String(value), valueW);
    ensureSpace(lines.length * 5 + 1);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...MUTED);
    doc.text(label, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...DARK);
    doc.text(lines, valueX, y);
    y += lines.length * 5 + 1;
  };

  const paragraph = (text) => {
    const lines = doc.splitTextToSize(text, pageW - margin * 2);
    ensureSpace(lines.length * 5 + 1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 1;
  };

  // Header
  const logo = await loadImage(Logo);
  if (logo) {
    const logoW = 50;
    const logoH = (logo.height / logo.width) * logoW;
    doc.addImage(logo, "PNG", margin, y, logoW, logoH);
    y += logoH + 4;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...DARK);
  doc.text("Proof of Service", margin, y + 4);
  doc.setFontSize(11);
  doc.text(`Order #${order?.invoice_no || ""}`, pageW - margin, y + 4, {
    align: "right",
  });
  y += 12;

  // Order
  heading("Order");
  row("Order number", order?.invoice_no);
  row("Requested on", fmtDate(order?.createdAt));
  row("Order status", ORDER_STATUS[order?.status] || order?.status);
  row("Work status", TASK_STATUS[order?.task_status] || order?.task_status);
  const completedEntry = (order?.status_history || [])
    .filter((h) => h?.status === "order_completed")
    .pop();
  if (completedEntry) {
    row(
      "Marked completed",
      `${fmtDate(completedEntry.changedAt)}${changedBy(completedEntry)}`
    );
  }
  row("Cancellation reason", order?.cancel_reason);

  // Customer
  const customer = order?.customer_id;
  heading("Customer");
  row("Name", fullName(customer));
  row("Email", customer?.email);
  row("Phone", customer?.phone);
  row("Account created", fmtDate(customer?.createdAt));

  // Service requested
  const vehicle = order?.vehicle_id?.[0];
  const address = order?.address_id?.[0];
  heading("Service requested");
  row("Service", order?.service_id?.name);
  row("Details", order?.details);
  row("Emergency message", order?.emergencyMessage);
  row(
    "Customer vehicle",
    [vehicle?.name, vehicle?.model, vehicle?.color].filter(Boolean).join(", ")
  );
  row("Vehicle registration", vehicle?.reg_no);
  row(
    "Service location",
    [
      address?.street_no,
      address?.address,
      address?.city,
      address?.state,
      address?.zip,
    ]
      .filter(Boolean)
      .join(", ")
  );

  // Service provided
  const staff = order?.staffs_id?.firstName ? order.staffs_id : extras.staff;
  const fleet = order?.fleet_id?.name ? order.fleet_id : extras.fleet;
  heading("Service provided");
  row("Technician", fullName(staff));
  row("Technician phone", staff?.phone);
  row(
    "Service vehicle",
    [fleet?.name, fleet?.registration_no && `(${fleet.registration_no})`]
      .filter(Boolean)
      .join(" ")
  );
  row("Towing", order?.towing ? "Yes" : "");

  // Payment - this order's own charge only, never the membership charge.
  const txn = order?.transaction?.[0]?.[0];
  const charge = txn?.paymentCharge;
  const card = charge?.payment_method_details?.card;
  heading("Payment");
  if (charge) {
    row(
      "Amount charged",
      charge?.amount != null
        ? `$${(charge.amount / 100).toFixed(2)} ${(charge.currency || "").toUpperCase()}`
        : ""
    );
    row("Charged on", charge?.created ? fmtDate(charge.created * 1000) : "");
    row("Stripe charge ID", charge?.id);
    row(
      "Card",
      card
        ? `${(card.brand || "").toUpperCase()} ending ${card.last4 || ""}${
            card.exp_month ? `, exp ${card.exp_month}/${card.exp_year}` : ""
          }${card.country ? `, issued in ${card.country}` : ""}`
        : ""
    );
    row("Cardholder name", charge?.billing_details?.name);
    row("Receipt email", charge?.receipt_email || charge?.billing_details?.email);
    row("CVC check", checkLabel(card?.checks?.cvc_check));
    row("Postal code check", checkLabel(card?.checks?.address_postal_code_check));
    row("3D Secure", card?.three_d_secure?.result);
    row("Payment status", charge?.status);
    row("Stripe receipt", charge?.receipt_url);
  } else {
    row("Order total", order?.total_amount ? `$${order.total_amount}` : "");
    paragraph("No card payment is recorded on this order.");
  }

  // Timeline
  const history = order?.status_history || [];
  heading("Status history");
  row("Order requested", fmtDate(order?.createdAt));
  history.forEach((h) => {
    const what = [
      ORDER_STATUS[h?.status] || h?.status,
      TASK_STATUS[h?.task_status] || h?.task_status,
    ]
      .filter(Boolean)
      .join(" / ");
    row(fmtDate(h?.changedAt), `${what}${changedBy(h)}`);
  });
  if (history.length === 0) {
    paragraph(
      "No step-by-step status changes were recorded for this order."
    );
  }

  // Notes
  const notes = (order?.notes?.[0] || []).filter((n) => n?.note);
  if (notes.length) {
    heading("Notes");
    notes.forEach((n) => row(fmtDate(n?.createdAt) || "Note", n.note));
  }

  // Footer on every page
  const pages = doc.getNumberOfPages();
  const generated = dateFormat(new Date(), "dd mmm yyyy, h:MM TT");
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `Generated from Roadside Response records on ${generated}`,
      margin,
      pageH - 8
    );
    doc.text(`Page ${i} of ${pages}`, pageW - margin, pageH - 8, {
      align: "right",
    });
  }

  doc.save(`${order?.invoice_no || "order"}-proof-of-service.pdf`);
};
